import {
  Modal,
  Box,
  Button,
  SpaceBetween,
  Grid,
  Tiles,
  TextContent,
  ColumnLayout,
} from "@cloudscape-design/components-themed";
import { COMMON_LABELS } from "../../default-constants";
import {
  ADDRESS_APP,
  ADDRESS_NG,
  ADDRESS_TAX,
  ADDRESS_TSR,
  ADDRESS_UNSELECTED,
  LABELS,
} from "../constants";
import {
  getAddressComment,
  getApiConfirmationResultForAddress,
  formatAddress,
  getAddress,
  isRequiresAddressConfirm,
  isSameStore,
  findRegisteredStore,
} from "../util";
import { ConfirmationStoreInfo } from "../../../features/api-interfaces";
import { Dispatch, SetStateAction } from "react";
import { isEqual, isUndefined } from "lodash";
import InfoField from "../../UI/InfoField";

interface IConfirmationResultModal {
  visible: boolean;
  setVisible: Dispatch<SetStateAction<boolean>>;
  storeList: ConfirmationStoreInfo[];
  setStoreList: Dispatch<SetStateAction<ConfirmationStoreInfo[] | undefined>>;
  modalStore: ConfirmationStoreInfo;
  registeredStoreList: ConfirmationStoreInfo[];
}

/**
 * 店舗 確認結果モーダル
 */
const AddressSelectModal = (props: IConfirmationResultModal) => {
  const {
    visible,
    setVisible,
    modalStore,
    storeList,
    setStoreList,
    registeredStoreList,
  } = props;
  const tsrAddress = getAddress(
    modalStore.addressList,
    Number(ADDRESS_TSR.value)
  );
  const taxAddress = getAddress(
    modalStore.addressList,
    Number(ADDRESS_TAX.value)
  );
  const appAddress = getAddress(
    modalStore.addressList,
    Number(ADDRESS_APP.value)
  );
  const isRegistered = !isUndefined(
    registeredStoreList.find((store) =>
      isSameStore(
        store,
        modalStore.applicationStatusNo,
        modalStore.individualHandlingReason
      )
    )
  );

  /**
   * 住所種別タイルの入力ハンドラ.
   * @param {number} addressType 選択した住所種別value
   */
  const confirmationAddressResultCheck = (addressType: number) => {
    setVisible(false);

    const newStoreList = storeList.map((store) => {
      const registeredStore = findRegisteredStore(
        registeredStoreList,
        store.applicationStatusNo,
        store.individualHandlingReason
      );

      if (registeredStore) {
        // 確認結果が登録済みの個別取扱事由はこのハンドラで変更しない
        return store;
      }

      if (
        isRequiresAddressConfirm(store.individualHandlingReason) &&
        isEqual(store.corporateNumber, modalStore.corporateNumber)
      ) {
        // 事業者住所確認の個別取扱で、同じ法人番号を持つレコードの確認結果と採用する住所を一括更新する
        return {
          ...store,
          confirmationResult: getApiConfirmationResultForAddress(addressType),
          comment:
            addressType === Number(ADDRESS_UNSELECTED.value)
              ? ""
              : getAddressComment(addressType, modalStore.comment ?? ""),
          addressType,
        } as ConfirmationStoreInfo;
      } else {
        return store;
      }
    });

    setStoreList(newStoreList);
  };

  return (
    <Modal
      onDismiss={() => setVisible(false)}
      visible={visible}
      size={"large"}
      footer={
        <Box float="right">
          <Button
            variant={"normal"}
            onClick={() =>
              confirmationAddressResultCheck(Number(ADDRESS_UNSELECTED.value))
            }
            disabled={isRegistered}
          >
            {COMMON_LABELS.BTNS.CLEAR}
          </Button>
        </Box>
      }
      header={LABELS.CONFIRMATION_ADDRESS_TITLE}
    >
      <SpaceBetween size={"xxl"} direction={"vertical"}>
        <SpaceBetween direction={"horizontal"} size={"l"}>
          <ColumnLayout columns={4}>
            <InfoField
              direction={"vertical"}
              size={"xxs"}
              tag={"h5"}
              values={[
                LABELS.COMPANY_NAME,
                LABELS.MERCHANT_NAME,
                LABELS.STORE_NAME,
              ]}
            />
          </ColumnLayout>
          <ColumnLayout columns={4}>
            <InfoField
              direction={"vertical"}
              size={"xxxs"}
              tag={"p"}
              values={[
                modalStore.companyName,
                modalStore.tradeName,
                modalStore.storeName,
              ]}
            />
          </ColumnLayout>
        </SpaceBetween>
        <Grid gridDefinition={[{ colspan: 2 }, { colspan: 6 }, { colspan: 4 }]}>
          <TextContent>
            <h5>{LABELS.TSR_NAME}</h5>
          </TextContent>
          <Box>{formatAddress(tsrAddress)}</Box>
          <Tiles
            items={[
              {
                value: ADDRESS_TSR.value,
                label: LABELS.UPDATE_TSR_BUTTON_NAME,
                disabled: isRegistered || isUndefined(tsrAddress),
              },
            ]}
            value={String(modalStore.addressType)}
            onChange={({ detail: { value } }) =>
              confirmationAddressResultCheck(Number(value))
            }
          />
        </Grid>
        <Grid gridDefinition={[{ colspan: 2 }, { colspan: 6 }, { colspan: 4 }]}>
          <TextContent>
            <h5>{LABELS.TAX_NAME}</h5>
          </TextContent>
          <Box>{formatAddress(taxAddress)}</Box>
          <Tiles
            items={[
              {
                value: ADDRESS_TAX.value,
                label: LABELS.UPDATE_TAX_BUTTON_NAME,
                disabled: isRegistered || isUndefined(taxAddress),
              },
            ]}
            value={String(modalStore.addressType)}
            onChange={({ detail: { value } }) =>
              confirmationAddressResultCheck(Number(value))
            }
          />
        </Grid>
        <Grid gridDefinition={[{ colspan: 2 }, { colspan: 6 }, { colspan: 4 }]}>
          <TextContent>
            <h5>{LABELS.MERCHANT_APPLICATION_NAME}</h5>
          </TextContent>
          <Box>{formatAddress(appAddress)}</Box>
          <Tiles
            items={[
              {
                value: ADDRESS_APP.value,
                label: LABELS.UPDATE_MERCHANT_BUTTON_NAME,
                disabled: isRegistered || isUndefined(appAddress),
              },
            ]}
            value={String(modalStore.addressType)}
            onChange={({ detail: { value } }) =>
              confirmationAddressResultCheck(Number(value))
            }
          />
        </Grid>
        <Grid gridDefinition={[{ colspan: 8 }, { colspan: 4 }]}>
          <TextContent>
            <h5>{LABELS.RETURN_DUE_DEFICIENCIES}</h5>
          </TextContent>
          <Tiles
            items={[
              {
                value: ADDRESS_NG.value,
                label: LABELS.UPDATE_NG_BUTTON_NAME,
                disabled: isRegistered,
              },
            ]}
            value={String(modalStore.addressType)}
            onChange={({ detail: { value } }) =>
              confirmationAddressResultCheck(Number(value))
            }
          />
        </Grid>
      </SpaceBetween>
    </Modal>
  );
};

export default AddressSelectModal;
